import { useEffect, useState } from "react";

import { Button, Typography } from "@mui/material";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Outlet, useLocation } from "react-router-dom";

import {
  BreadcrumbChevronIcon,
  BreadcrumbDropdownMenu,
  BreadcrumbHoverRoot,
  BreadcrumbMenuLinkItem,
  describeMode,
} from "../components/BreadcrumbDropdownMenu";
import { BreadcrumbHeaderListing, Title } from "../components/Header";
import { LinkRouter } from "../components/Links";
import {
  breadcrumbCurrentLabelSx,
  breadcrumbCurrentTriggerSx,
  breadcrumbTitleSx,
} from "../components/dropdownMenuStyles";

import type { AeronetViewProps } from "../types";
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

export const breadcrumbMenu: { [key: string]: string } = {
  "/components/parts": "Parts",
  "/components/flying": "Flying",
  "/components/removed": "Removed",
  "/components/rotable": "Rotable",
  "/components/retired": "Retired",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 *
 * Keyed by ABSOLUTE path, exactly like `breadcrumbMenu`.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  "/components/parts": "Serial counts per part",
  "/components/flying": "Fitted to aircraft",
  "/components/removed": "Off aircraft, by location",
  "/components/rotable": "Rotables by location",
  "/components/retired": "Retired serials by location",
};

const allRoutes: { [key: string]: string } = {
  ...{ "/components": "Components" },
  ...breadcrumbMenu,
};

interface ComponentLayoutProps extends AeronetViewProps {
  originalUrl: string;
}

const ComponentLayout: React.FC<ComponentLayoutProps> = () => {
  const location = useLocation();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "breadcrumbMenu",
  });

  const [pathLinks, setPathLinks] = useState<string[]>([]);

  useEffect(() => {
    const _pathLinks: string[] = [];
    const paths = location.pathname.split("/");
    let pathLinkCount = 0;
    for (let i = 0; i < paths.length; i++) {
      let path = paths[i];
      let previousPath = _pathLinks[pathLinkCount - 1] ?? "";
      if (previousPath) {
        path = previousPath + "/" + path;
      } else {
        if (path) {
          path = "/" + path;
        }
      }
      if (path) {
        _pathLinks.push(path);
        pathLinkCount++;
      }
    }
    setPathLinks(_pathLinks);
  }, [location.pathname]);

  return (
    <>
      <BreadcrumbHeaderListing aria-label="breadcrumb">
        <Title sx={breadcrumbTitleSx}>
          {pathLinks.map((path) => {
            if (path === location.pathname) {
              return (
                <BreadcrumbHoverRoot key={path} popupState={popupState}>
                  <Button
                    endIcon={<BreadcrumbChevronIcon />}
                    sx={breadcrumbCurrentTriggerSx}
                  >
                    <Typography
                      variant="h2"
                      component="span"
                      sx={breadcrumbCurrentLabelSx}
                    >
                      {allRoutes[location.pathname]}
                    </Typography>
                  </Button>
                  <BreadcrumbDropdownMenu popupState={popupState}>
                    {Object.entries(breadcrumbMenu).map(([key, value]) => (
                      <BreadcrumbMenuLinkItem
                        key={key.split("/")[2]}
                        to={key}
                        label={value}
                        description={describeMode(MODE_DESCRIPTIONS, key)}
                        selected={location.pathname === key}
                        end={key === "/components"}
                        onClick={popupState.close}
                      />
                    ))}
                  </BreadcrumbDropdownMenu>
                </BreadcrumbHoverRoot>
              );
            }
            return (
              <LinkRouter to={path} key={path}>
                {allRoutes[path]} /
              </LinkRouter>
            );
          })}
        </Title>
      </BreadcrumbHeaderListing>
      <Outlet />
    </>
  );
};

export default ComponentLayout;
